import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Footer from "@/components/Footer";
import ListingCard from "@/components/ListingCard";
import WalletSection from "@/components/WalletSection";
import { toast } from "sonner";
import {
  Calendar, MapPin, Users, Heart, Plane, Loader2, CheckCircle2, Clock, XCircle, Wallet, Sparkles, Car, Truck, Fuel, Star, X, MessageSquare, Images, Lock,
} from "lucide-react";
import { fileUrl } from "@/lib/api";
import TripChat from "@/components/TripChat";

const STATUS = {
  confirmed: { label: "Confirmed", cls: "text-emerald-400 border-emerald-400/30", Icon: CheckCircle2 },
  pending: { label: "Pending payment", cls: "text-amber-400 border-amber-400/30", Icon: Clock },
  cancelled: { label: "Cancelled", cls: "text-[#FF5E7E] border-[#FF5E7E]/30", Icon: XCircle },
};

export default function TravelerDashboard() {
  const { user, refreshUser } = useAuth();
  const [tab, setTab] = useState("bookings");
  const [bookings, setBookings] = useState([]);
  const [carBookings, setCarBookings] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewCar, setReviewCar] = useState(null); // booking being reviewed
  const [chat, setChat] = useState(null);
  const [memBooking, setMemBooking] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    const c = searchParams.get("chat");
    if (c) {
      setChat(c);
      searchParams.delete("chat");
      setSearchParams(searchParams, { replace: true });
    }
    refreshUser();
  }, []); // eslint-disable-line

  const load = () => {
    setLoading(true);
    Promise.all([api.get("/bookings/me"), api.get("/favorites"), api.get("/car-bookings/me")])
      .then(([b, f, c]) => { setBookings(b.data); setFavorites(f.data); setCarBookings(c.data); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const cancel = async (id) => {
    try {
      const { data } = await api.post(`/bookings/${id}/cancel`);
      toast.success(data.message || "Booking cancelled");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const requestRefund = async (id) => {
    try {
      const { data } = await api.post(`/bookings/${id}/refund-claim`);
      toast.success(data.message || "Refund claim submitted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const removeFav = async (id) => {
    try { await api.delete(`/favorites/${id}`); setFavorites((f) => f.filter((l) => l.id !== id)); toast("Removed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const upcoming = bookings.filter((b) => b.status !== "cancelled").length;

  return (
    <div className="min-h-screen bg-[#070A18]">
      <div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto pt-10 pb-16">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl btn-neon flex items-center justify-center font-heading text-2xl font-bold">
              {user?.name?.[0]}
            </div>
            <div>
              <h1 className="font-heading text-2xl md:text-3xl font-bold text-white">Hi, {user?.name?.split(" ")[0]} 👋</h1>
              <p className="text-slate-400 text-sm">{upcoming} active trips · {favorites.length} saved</p>
            </div>
          </div>
          <div className="glass rounded-2xl px-5 py-3 flex items-center gap-3" data-testid="header-points">
            <div className="w-10 h-10 rounded-xl bg-grad-cool flex items-center justify-center text-white font-black">★</div>
            <div>
              <p className="font-mono-caption text-[10px] uppercase tracking-widest text-cyan-300">Points</p>
              <p className="font-heading text-xl font-extrabold text-gradient-cool leading-none">{(user?.points ?? 0).toLocaleString()}</p>
            </div>
          </div>
        </div>

        <div className="flex gap-2 mb-8 border-b border-[#232A4D]">
          {[
            { key: "bookings", label: "My Trips", icon: Plane },
            { key: "cars", label: "My Cars", icon: Car },
            { key: "favorites", label: "Saved", icon: Heart },
            { key: "wallet", label: "Wallet & Points", icon: Wallet },
          ].map((t) => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 -mb-px transition-colors ${
                tab === t.key ? "border-cyan-400 text-cyan-300" : "border-transparent text-slate-400 hover:text-white"
              }`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "wallet" ? (
          <WalletSection points={user?.points} />
        ) : loading ? (
          <div className="flex justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-cyan-400" /></div>
        ) : tab === "bookings" ? (
          bookings.length === 0 ? (
            <EmptyState label="No trips yet" cta />
          ) : (
            <div className="space-y-4">
              {bookings.map((b) => {
                const st = STATUS[b.status] || STATUS.pending;
                return (
                  <Link key={b.id} to={`/booking/${b.id}/confirmation`} data-testid={`booking-row-${b.id}`} className="glass glass-hover rounded-2xl p-4 flex flex-col sm:flex-row gap-4">
                    <img src={b.listing_image} alt="" className="w-full sm:w-40 h-32 rounded-xl object-cover" />
                    <div className="flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <h3 className="font-heading font-semibold text-white text-lg">{b.listing_title}</h3>
                          <div className="flex flex-wrap gap-4 mt-2 text-xs text-slate-400">
                            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5 text-cyan-400" /> {b.island}</span>
                            <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-emerald-400" /> {b.date} · {b.time}</span>
                            <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5 text-violet-400" /> {b.guests} guests</span>
                          </div>
                        </div>
                        <span className={`shrink-0 flex items-center gap-1.5 text-xs px-3 py-1 rounded-full glass border ${st.cls}`}>
                          <st.Icon className="w-3.5 h-3.5" /> {st.label}
                        </span>
                      </div>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#1D2640]">
                        <span className="font-heading font-bold text-white">${b.gross_amount?.toLocaleString()} <span className="text-xs text-slate-500 font-normal">· {b.guests} traveler{b.guests > 1 ? "s" : ""}</span></span>
                        <div className="flex items-center gap-3">
                          <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setChat(b.listing_id ? b.id : b.id); }} data-testid={`trip-chat-${b.id}`}
                            className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-fuchsia-500/15 text-fuchsia-300 hover:bg-fuchsia-500/25 transition-colors flex items-center gap-1">
                            <MessageSquare className="w-3.5 h-3.5" /> Chat
                          </button>
                          {b.memories_count > 0 && (
                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); setMemBooking(b); }} data-testid={`trip-memories-${b.id}`}
                              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25 transition-colors flex items-center gap-1">
                              <Images className="w-3.5 h-3.5" /> Memories · {b.memories_count}{!b.memories_unlocked && <Lock className="w-3 h-3" />}
                            </button>
                          )}
                          {b.refund_status === "refunded" && <span className="text-xs text-emerald-400">Refunded</span>}
                          {b.refund_status === "requested" && <span className="text-xs text-cyan-300">Refund requested</span>}
                          {b.status !== "cancelled" && b.refund_status !== "requested" && b.refund_status !== "refunded" && (
                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); cancel(b.id); }} data-testid={`cancel-booking-${b.id}`}
                              className="text-xs text-slate-400 hover:text-[#FF5E7E] transition-colors">Cancel trip</button>
                          )}
                          {b.refund_claim_open && b.refund_status !== "requested" && b.refund_status !== "refunded" ? (
                            <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); requestRefund(b.id); }} data-testid={`request-refund-${b.id}`}
                              className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#FF5E7E]/15 text-[#FF5E7E] hover:bg-[#FF5E7E]/25 transition-colors">Request refund</button>
                          ) : (b.completion_date && b.refund_status === "none" && b.status !== "cancelled" && b.payment_status === "paid") ? (
                            <span className="text-[11px] text-slate-600" data-testid={`refund-closed-${b.id}`} title={`Refund claims close ${b.refund_claim_until}`}>Refund window closed</span>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          )
        ) : tab === "favorites" ? (
          favorites.length === 0 ? (
            <EmptyState label="No saved listings yet" cta />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {favorites.map((l) => <ListingCard key={l.id} listing={l} onFav={removeFav} isFav />)}
            </div>
          )
        ) : null}

        {tab === "cars" && !loading && (
          carBookings.length === 0 ? (
            <div className="text-center py-24 glass rounded-2xl">
              <Car className="w-10 h-10 text-slate-600 mx-auto mb-3" />
              <p className="font-heading text-xl text-white mb-2">No car rentals yet</p>
              <p className="text-slate-400 text-sm mb-6">Rent a car and explore the island on your own schedule.</p>
              <Link to="/cars" className="btn-neon rounded-full px-6 py-2.5 font-semibold text-sm inline-block">Browse cars</Link>
            </div>
          ) : (
            <div className="space-y-4" data-testid="my-car-bookings">
              {carBookings.map((b) => (
                <Link key={b.id} to={`/car-booking/${b.id}/confirmation`} data-testid={`car-booking-row-${b.id}`} className="glass glass-hover rounded-2xl p-4 flex flex-col sm:flex-row gap-4">
                  <img src={b.car_image ? fileUrl(b.car_image) : "https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=85"} alt="" className="w-full sm:w-40 h-32 rounded-xl object-cover" />
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="font-heading font-semibold text-white text-lg">{b.car_name}</h3>
                        <div className="flex flex-wrap gap-4 mt-2 text-xs text-slate-400">
                          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5 text-emerald-400" /> {b.pickup_date} → {b.dropoff_date}</span>
                          <span className="flex items-center gap-1">{b.delivery_option === "delivery" ? <Truck className="w-3.5 h-3.5 text-cyan-400" /> : <MapPin className="w-3.5 h-3.5 text-cyan-400" />} {b.delivery_option === "delivery" ? "Delivery" : "Branch pickup"}</span>
                          <span className="flex items-center gap-1"><Fuel className="w-3.5 h-3.5 text-fuchsia-400" /> {b.fuel_policy}</span>
                        </div>
                      </div>
                      <span className="shrink-0 text-xs px-3 py-1 rounded-full glass border text-cyan-300 border-cyan-300/30 uppercase tracking-widest">{(b.status || "").replace("_", " ")}</span>
                    </div>
                    <div className="flex items-center justify-between mt-4 pt-3 border-t border-[#1D2640]">
                      <span className="font-heading font-bold text-white">${b.total?.toLocaleString()}</span>
                      <span className={`text-xs ${b.deposit?.status === "released" ? "text-emerald-400" : "text-amber-400"}`}>deposit: {b.deposit?.status}</span>
                    </div>
                    {b.status === "completed" && (
                      b.reviewed ? (
                        <span className="inline-flex items-center gap-1.5 mt-3 text-xs text-emerald-400"><Star className="w-3.5 h-3.5 fill-emerald-400" /> Review submitted</span>
                      ) : (
                        <button
                          data-testid={`rate-car-${b.id}`}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); setReviewCar(b); }}
                          className="inline-flex items-center gap-1.5 mt-3 text-xs font-semibold px-4 py-2 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors"
                        >
                          <Star className="w-3.5 h-3.5" /> Rate this car
                        </button>
                      )
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )
        )}
      </div>
      {reviewCar && <ReviewModal booking={reviewCar} onClose={() => setReviewCar(null)} onDone={() => { setReviewCar(null); load(); }} />}
      {chat && <TripChat bookingId={chat} onClose={() => setChat(null)} />}
      {memBooking && <MemoriesGallery booking={memBooking} onClose={() => setMemBooking(null)} onUnlocked={load} />}
      <Footer />
    </div>
  );
}

function MemoriesGallery({ booking, onClose, onUnlocked }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unlocking, setUnlocking] = useState(false);

  const fetchMems = () => {
    setLoading(true);
    api.get(`/bookings/${booking.id}/memories`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Could not load memories"))
      .finally(() => setLoading(false));
  };
  useEffect(fetchMems, [booking.id]);

  const unlock = async () => {
    setUnlocking(true);
    try {
      const { data: res } = await api.post(`/bookings/${booking.id}/memories/unlock`, { origin_url: window.location.origin });
      if (res.unlocked) {
        toast.success("Memories unlocked! Enjoy your full-resolution photos 🎉");
        fetchMems();
        onUnlocked && onUnlocked();
      } else if (res.checkout_url) {
        window.location.href = res.checkout_url;
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setUnlocking(false); }
  };

  const mems = data?.memories || [];
  const unlocked = data?.unlocked;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose}>
      <div className="glass neon-border rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="memories-gallery">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading text-xl font-bold text-white flex items-center gap-2"><Images className="w-5 h-5 text-cyan-400" /> Trip memories</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-slate-400 text-sm mb-5">{booking.listing_title} · {mems.length} photo(s)</p>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-cyan-400" /></div>
        ) : mems.length === 0 ? (
          <p className="text-slate-400 text-sm py-10 text-center">No memories yet.</p>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {mems.map((m) => (
                <a key={m.id} href={unlocked ? fileUrl(m.url) : undefined} target="_blank" rel="noreferrer"
                  className={`relative aspect-square rounded-xl overflow-hidden border border-[#232A4D] ${unlocked ? "cursor-zoom-in" : "cursor-default"}`}
                  data-testid={`memory-${m.id}`}>
                  <img src={fileUrl(m.preview_url)} alt="" className="w-full h-full object-cover" />
                  {!unlocked && (
                    <div className="absolute inset-0 bg-black/25 flex items-center justify-center">
                      <Lock className="w-6 h-6 text-white/90" />
                    </div>
                  )}
                </a>
              ))}
            </div>
            {!unlocked && (
              <div className="mt-6 rounded-xl bg-gradient-to-r from-cyan-500/10 to-blue-600/10 border border-cyan-400/25 p-4 text-center">
                <p className="text-sm text-slate-300 mb-3">These are watermarked previews. Unlock all {mems.length} full-resolution photos for just <span className="text-cyan-300 font-bold">$1</span>.</p>
                <button onClick={unlock} disabled={unlocking} data-testid="memories-unlock-btn"
                  className="btn-neon rounded-full px-6 py-2.5 font-semibold text-sm inline-flex items-center gap-2 disabled:opacity-60">
                  {unlocking ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />} Unlock all for $1
                </button>
              </div>
            )}
            {unlocked && (
              <p className="mt-5 text-center text-xs text-emerald-400 flex items-center justify-center gap-1.5">
                <CheckCircle2 className="w-4 h-4" /> Unlocked — tap any photo to view full resolution
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function ReviewModal({ booking, onClose, onDone }) {
  const [rating, setRating] = useState(5);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      await api.post(`/cars/${booking.car_id}/reviews`, { booking_id: booking.id, rating, comment });
      toast.success("Thanks for your review!");
      onDone();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={onClose}>
      <div className="glass neon-border rounded-2xl w-full max-w-md p-6" onClick={(e) => e.stopPropagation()} data-testid="review-modal">
        <div className="flex items-center justify-between mb-1">
          <h2 className="font-heading text-xl font-bold text-white">Rate your rental</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-slate-400 text-sm mb-5">{booking.car_name}</p>
        <div className="flex items-center gap-2 mb-5" data-testid="review-stars">
          {[1, 2, 3, 4, 5].map((n) => (
            <button key={n} type="button" data-testid={`review-star-${n}`}
              onMouseEnter={() => setHover(n)} onMouseLeave={() => setHover(0)} onClick={() => setRating(n)}>
              <Star className={`w-8 h-8 transition-colors ${n <= (hover || rating) ? "fill-[#22E67A] text-[#22E67A]" : "text-slate-600"}`} />
            </button>
          ))}
        </div>
        <textarea data-testid="review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={4}
          placeholder="How was the car and the pickup experience?" className="w-full bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50 resize-none" />
        <button onClick={submit} disabled={saving} data-testid="review-submit" className="w-full btn-neon rounded-xl py-3 font-semibold flex items-center justify-center gap-2 mt-4 disabled:opacity-60">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Star className="w-4 h-4" />} Submit review
        </button>
      </div>
    </div>
  );
}

function EmptyState({ label, cta }) {
  return (
    <div className="text-center py-24 glass rounded-2xl">
      <p className="font-heading text-xl text-white mb-2">{label}</p>
      <p className="text-slate-400 text-sm mb-6">Start exploring extraordinary Caribbean escapes.</p>
      {cta && <Link to="/browse" className="btn-neon rounded-full px-6 py-2.5 font-semibold text-sm inline-block">Browse listings</Link>}
    </div>
  );
}
